import { useState, forwardRef, useRef, useEffect } from 'react'
import { CloudArrowUpIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline'
import AvatarEditor from 'react-avatar-editor'
import { useTranslation } from 'libs/i18n'
import { Button, Dialog } from 'shared/components'
import { Label } from 'shared/atoms'
import { defaultConfig } from 'config/default'
import ImageUploading from 'react-images-uploading'

type FileUploaderProps = {
	maxSize?: number
	label?: string
	images: any
	setImages: any
	onChange: (e: any) => void
}

const actionClassName =
	'grid h-7 w-7 place-items-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [&>svg]:h-3.5 [&>svg]:w-3.5'

const secondaryButtonClassName =
	'h-9 rounded-md border-border bg-card px-4 text-sm font-medium text-foreground shadow hover:bg-muted'
const primaryButtonClassName =
	'h-9 rounded-md px-4 text-sm font-medium shadow transition-colors hover:bg-primary/90'

const defaultImageData = {
	image: null,
	position: { x: 0.5, y: 0.5 },
	scale: 1,
	rotate: 0,
	width: 600,
	height: 800,
	allowZoomOut: false
}

export const FileUploader = forwardRef(
	({ label, images, setImages, onChange, ...rest }: FileUploaderProps, _ref) => {
		const data = rest as any

		const [isEditModalOpen, setEditModalOpen] = useState(false)
		const imageRef = useRef<any | null>(null)
		const [editedImageData, setEditedImageData] = useState<any>(defaultImageData)
		const { t } = useTranslation()

		const editImage = (index: number) => {
			const image = new File([images[index].file], images[index].file.name, {
				type: images[index].file.type
			})
			setEditedImageData({
				...editedImageData,
				image
			})
			setEditModalOpen(true)
		}

		const rotateLeft = () => {
			setEditedImageData({
				...editedImageData,
				rotate: editedImageData.rotate - 90
			})
		}

		const rotateRight = () => {
			setEditedImageData({
				...editedImageData,
				rotate: editedImageData.rotate + 90
			})
		}

		const handlePositionChange = (position: any) => {
			setEditedImageData({
				...editedImageData,
				position
			})
		}

		const handleScale = (e: any) => {
			const scale = parseFloat(e.target.value)
			setEditedImageData({
				...editedImageData,
				scale
			})
		}

		const saveEditedImage = () => {
			if (imageRef?.current) {
				const canvas = imageRef.current.getImage()
				canvas.toBlob((blob: any) => {
					const fileToChangeIndex = images.findIndex(
						(image: any) => image.file?.name === editedImageData.image.name
					)
					const file = new File([blob], editedImageData.image.name, {
						type: images[fileToChangeIndex].file.type
					})
					const filesClone = [...images]

					filesClone.splice(fileToChangeIndex, 1, {
						dataUrl: '',
						file
					})

					setImages(filesClone)
					setEditedImageData(defaultImageData)
					setEditModalOpen(false)
					onChange(filesClone)
				})
			}
		}

		useEffect(() => {
			if (data.value) {
				setImages([
					{
						dataURL: '',
						file: data.value
					}
				])
			}
		}, [data.value])

		const handleImageChange = (imageList: any) => {
			setImages(imageList)
			onChange(imageList)
		}

		return (
			<div className="flex flex-col gap-y-1.5">
				<Label text={label} />
				<ImageUploading
					value={images}
					onChange={handleImageChange}
					maxNumber={1}
					maxFileSize={defaultConfig.maxFileSize}>
					{({ imageList, onImageUpload, onImageUpdate, onImageRemove, dragProps }) => (
						<>
							<button
								className="flex w-full flex-col items-center rounded-lg border border-dashed border-border bg-muted/40 px-4 py-6 text-center transition-colors hover:border-primary hover:bg-primary-soft/40"
								type="button"
								{...dragProps}
								onClick={imageList.length ? () => onImageUpdate(0) : onImageUpload}>
								<CloudArrowUpIcon className="h-6 w-6 text-primary" />
								<span className="mt-1.5 font-display text-[13px] font-semibold text-primary">
									{t('Upload a file')}
								</span>
								<span className="text-[11px] text-muted-foreground">
									{t('Max {{ size }} MB', { size: 40 })}
								</span>
							</button>
							{imageList.map((image, index) => (
								<div key={index}>
									<div className="mt-3 flex items-start gap-2">
										<button
											type="button"
											aria-label={t('Edit')}
											className={actionClassName}
											onClick={() => editImage(index)}>
											<PencilIcon />
										</button>
										<button
											type="button"
											aria-label={t('Delete')}
											className={actionClassName}
											onClick={() => onImageRemove(index)}>
											<XMarkIcon />
										</button>
									</div>
									<div className="mt-3 h-16 w-16 overflow-hidden rounded-md border border-border bg-card">
										<img
											className="h-full w-full object-cover"
											src={URL.createObjectURL(image.file!)}
											alt=""
											onLoad={() => {
												URL.revokeObjectURL(URL.createObjectURL(image.file!))
											}}
										/>
									</div>
								</div>
							))}
						</>
					)}
				</ImageUploading>
				{isEditModalOpen && (
					<Dialog
						isOpened={isEditModalOpen}
						ariaLabel={t('Edit image')}
						className="max-w-fit"
						closeModal={() => setEditModalOpen(false)}>
						<div className="px-6 pb-4 pt-6">
							<h2 className="pr-8 font-display text-lg font-bold tracking-tight text-foreground">
								{t('Edit image')}
							</h2>
							<p className="mt-1.5 text-[13px] leading-5 text-muted-foreground">
								{t('Drag to reposition, then zoom or rotate the image.')}
							</p>
						</div>
						<button
							type="button"
							aria-label={t('Close')}
							className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
							onClick={() => setEditModalOpen(false)}>
							<XMarkIcon className="h-4 w-4 stroke-2" />
						</button>
						<div className="flex min-h-0 flex-1 flex-col items-center gap-4 overflow-y-auto px-6 pb-5">
							{editedImageData?.image && (
								<div className="overflow-hidden rounded-md border border-border">
									<AvatarEditor
										ref={imageRef}
										image={editedImageData.image}
										width={editedImageData.width}
										height={editedImageData.height}
										rotate={editedImageData.rotate}
										scale={parseFloat(editedImageData.scale)}
										position={editedImageData.position}
										onPositionChange={handlePositionChange}
									/>
								</div>
							)}
							<input
								name="scale"
								type="range"
								aria-label={t('Zoom')}
								className="w-full max-w-xs accent-primary"
								onChange={handleScale}
								min={editedImageData.allowZoomOut ? '0.1' : '1'}
								max="2"
								step="0.01"
								defaultValue="1"
							/>
						</div>
						<div className="flex items-center justify-end gap-2 border-t border-border bg-background/60 px-6 py-4">
							<Button styleType="outline" className={secondaryButtonClassName} onClick={rotateLeft}>
								{t('Rotate left')}
							</Button>
							<Button
								styleType="outline"
								className={secondaryButtonClassName}
								onClick={rotateRight}>
								{t('Rotate right')}
							</Button>
							<Button className={primaryButtonClassName} onClick={saveEditedImage}>
								{t('Save')}
							</Button>
						</div>
					</Dialog>
				)}
			</div>
		)
	}
)
